import { Mic, Square } from "lucide-react";

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useProctoring } from "@/hooks/useProctoring";
import { ProctorLive } from "@/components/ProctorLive";
import { ScoreRing, CompetencyBar } from "@/components/shared";
import { copilotReturn } from "@/lib/embedded";
import { motion, AnimatePresence } from "framer-motion";
import { browserSupport, BrowserBlockGate, FullscreenGate, PausedOverlay, RuleWarning } from "@/components/exam/ExamGates";
import { inSeb, SebLaunchGate, SebVerifyGate } from "@/components/exam/SebGates";
import {
  Loader2, ShieldCheck, Sparkles, ArrowLeft, ArrowRight, CheckCircle2, Clock,
  X, Circle, FlaskConical, ListChecks, Maximize2, AlertTriangle, ScanFace, Upload, MonitorPlay,
} from "lucide-react";

const DIFF = {
  easy: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  hard: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const CALM = {
  multiple_faces: "We can see a second person. Please make sure you're alone.",
  no_face: "We can't see your face. Please sit in front of the camera.",
  looking_away: "Please keep your eyes on the test screen.",
  phone_detected: "A phone seems to be in view. Please put it away.",
  headphones_detected: "Headphones or earbuds seem to be in use. Please take them off.",
  device_detected: "Another device seems to be in view. Please move it away.",
  paper_detected: "Books or notes seem to be in view. Please put them away.",
  second_screen: "A second screen seems to be in view. Please use one screen only.",
  tab_switch: "Please stay on the test tab.",
  fullscreen_exit: "Please return to full screen.",
  talking: "We picked up talking. Please work quietly on your own.",
  multiple_voices: "We picked up other voices. Please make sure you're alone.",
};

export default function ExamRoom() {
  const { candId, jobId, trialId } = useParams();
  const trial = !!trialId;
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [test, setTest] = useState(null);
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [idDone, setIdDone] = useState(false);
  const [screenSkipped, setScreenSkipped] = useState(false);
  const [lockStarted, setLockStarted] = useState(false);
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  const support = useMemo(() => browserSupport(), []);
  const inSebApp = useMemo(() => inSeb(), []);
  const [sebInfo, setSebInfo] = useState(null);
  const [sebOk, setSebOk] = useState(false);
  const submitRef = useRef(false);
  const cacheKey = trial ? `trial_${trialId}` : `exam_${candId}`;

  // ---- load
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (trial) {
          const { data } = await api.get(`/test/trial/${trialId}`);
          if (!alive) return;
          setJob({ title: data.job_title });
          setTest({ sections: data.sections, rubric: data.rubric, source: data.source, time_limit_min: data.time_limit_min, proctoring_policy: data.proctoring_policy });
        } else {
          const { data: apps } = await api.get("/me/applications");
          const a = (apps || []).find((x) => x.id === candId);
          if (!a) { nav("/candidate/applications"); return; }
          if (a.assessment?.status === "scoring" || a.assessment?.status === "completed") { nav("/candidate/applications"); return; }
          setJob(a.job); setConsent(a.consent || {});
          const { data: at } = await api.get(`/jobs/${a.job_id}/active-test`);
          setTest(at.active || null);
          if (at.active?.proctoring_policy?.require_seb) { const { data: s } = await api.get(`/candidates/${candId}/seb`); if (alive) setSebInfo(s); }
        }
        const cached = localStorage.getItem(cacheKey);
        if (cached) try { setAnswers(JSON.parse(cached)); } catch { /* ignore */ }
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load the assessment");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [candId, trialId]);

  // ---- fullscreen tracking (best-effort for normal tests; required in lockdown)
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => { document.removeEventListener("fullscreenchange", onFs); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); };
  }, []);
  const enterFs = () => document.documentElement.requestFullscreen?.().then(() => setIsFs(true)).catch(() => toast.error("Full screen was blocked. Allow it in your browser and try again."));

  // Trials run the FULL proctored experience so recruiters can actually test the
  // AI proctoring shell; candidate runs respect their consent AND the per-assessment policy.
  const policy = test?.proctoring_policy || {};
  const sebMode = !trial && !!policy.require_seb && !sebInfo?.exempt;
  const lockdown = !!policy.lockdown || (!trial && !!policy.require_seb && !!sebInfo?.exempt);
  const lockGates = lockdown && !(sebMode && inSebApp);
  const browserBlocked = lockGates && !support.ok;
  const proctored = trial ? true : !!(consent?.proctoring && !consent?.alternative_path);
  const sebReady = !sebMode || (inSebApp && sebOk);
  const gatesOpen = sebReady && !browserBlocked && (!lockGates || lockStarted);
  const screenSkip = screenSkipped || (sebMode && inSebApp);
  const proctor = useProctoring(candId, { enabled: proctored && !loading && !result && gatesOpen, policy, trialId, analyzeEvery: 3 });
  const { videoRef, screenRef, events, status, screenStatus, needsScreen, startScreen, finalize } = proctor;
  const warnN = policy.warn_threshold || 3;
  const warned = trial && proctored && events.length >= warnN;
  const [pstate, setPstate] = useState(null);
  const lastWarnRef = useRef(null);
  const paused = !!pstate?.paused;
  useEffect(() => {
    if (!proctored || trial || !candId || result) return;
    const tick = () => api.get(`/candidates/${candId}/proctor/state`).then(({ data }) => {
      setPstate(data);
      if (data.last && lastWarnRef.current && data.last.id !== lastWarnRef.current) toast.warning(CALM[data.last.type] || `${data.last.label}. Please keep your setup as agreed.`, { duration: 7000 });
      if (data.last) lastWarnRef.current = data.last.id;
      if (data.force_submit && !submitRef.current) { toast.message("This assessment has ended under its monitoring rules and your answers were submitted."); submitRef.current = false; submitForcedRef.current?.(); }
    }).catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [proctored, trial, candId, result]);
  const submitForcedRef = useRef(null);
  const inRoom = !loading && !err && !result && gatesOpen && !(!trial && policy.identity_check && !idDone) && !(proctored && needsScreen && !screenSkip);
  const fsLost = lockGates && inRoom && !isFs;
  // ---- timer: only runs inside the room, and stops while a recruiter has the test paused
  useEffect(() => {
    if (!inRoom || paused) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [inRoom, paused]);

  const questions = useMemo(() => {
    const out = [];
    (test?.sections || []).forEach((s, si) => (s.items || []).forEach((it, ii) =>
      out.push({ key: `${si}-${ii}`, section: s.title, type: s.type, prompt: it.prompt, assesses: it.assesses, difficulty: it.difficulty, format: it.format || "writing", options: it.options || [] })));
    if (out.length === 0 && job) out.push({ key: "free", section: "Assessment", prompt: `Describe a challenging problem relevant to the ${job.title || "role"} and how you approached it — focus on your reasoning and measurable impact.` });
    return out;
  }, [test, job]);

  const setAns = (k, v) => setAnswers((a) => { const n = { ...a, [k]: v }; localStorage.setItem(cacheKey, JSON.stringify(n)); return n; });
  const answered = questions.filter((q) => (answers[q.key] || "").trim()).length;
  const total = questions.length || 1;
  const allAnswered = answered >= questions.length && questions.length > 0;
  const q = questions[idx];
  const limitSec = (test?.time_limit_min || 0) * 60;
  const remaining = limitSec ? Math.max(0, limitSec - elapsed) : null;

  const backTo = copilotReturn();
  const exit = async () => {
    if (!trial && !result) {
      const dirty = Object.values(answers).some((v) => (v || "").trim());
      if (dirty && !window.confirm("Leave the assessment? Your answers are autosaved, but monitoring will stop and you'll need to resume to finish.")) return;
    }
    try { await finalize?.(); } catch { toast.error("Recording could not be saved. Stay on this page and retry to preserve the recording."); return; }
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    nav(trial ? backTo || `/recruiter/jobs/${jobId}/test` : "/candidate/applications");
  };

  const combined = () => questions.map((x) => `Q: ${x.prompt}\nA: ${answers[x.key] || ""}`).join("\n\n");
  submitForcedRef.current = () => submit(true);

  const submit = async (force = false) => {
    if (!force && !allAnswered) { toast.error("Please answer all questions before submitting"); return; }
    if (submitRef.current) return;
    submitRef.current = true;
    setBusy(true);
    try {
      await finalize();
      if (trial) {
        const { data } = await api.post(`/test/trial/${trialId}/score`, { note: combined(), decision: "complete", proctor_events: events, answers });
        setResult(data.result);
        localStorage.removeItem(cacheKey);
      } else {
        await api.post(`/candidates/${candId}/assessment`, { decision: "complete", note: combined(), answers });
        localStorage.removeItem(cacheKey);
        if (inSebApp) { window.location.href = "/seb/quit"; return; }
        toast.success(force ? "Time's up — your answers were submitted." : "Submitted! Your response is being scored.");
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
        nav("/candidate/applications");
      }
    } catch (e) { submitRef.current = false; toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (limitSec && remaining === 0 && !loading && !result) submit(true);
    // eslint-disable-next-line
  }, [remaining, limitSec, loading, result]);

  if (loading) {
    return (
      <div className="dark fixed inset-0 z-[60] bg-[#070809] flex items-center justify-center text-foreground" data-testid="exam-loading">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
          <div className="text-sm text-muted-foreground">Preparing your assessment room…</div>
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="dark fixed inset-0 z-[60] bg-[#070809] flex items-center justify-center p-6 text-foreground" data-testid="exam-error">
        <div className="text-center space-y-4 max-w-sm">
          <div className="text-rose-400 text-sm">{err}</div>
          <Button variant="outline" onClick={exit} data-testid="exam-error-back">Go back</Button>
        </div>
      </div>
    );
  }

  // ---- trial result screen
  if (trial && result) {
    return (
      <div className="dark fixed inset-0 z-[60] bg-[#070809] overflow-y-auto text-foreground" data-testid="exam-trial-result">
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
            <h1 className="text-2xl font-display font-bold">Trial run complete</h1>
            <p className="text-sm text-muted-foreground">The full candidate experience works end-to-end: {questions.length} question{questions.length === 1 ? "" : "s"}, autosave, proctoring shell and scoring pipeline.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-4">
              {result.overall_score != null && <ScoreRing score={result.overall_score} size={56} label="score" />}
              <div>
                <div className="font-display font-semibold">Scoring preview</div>
                <div className="text-xs text-muted-foreground">{result.confidence ? `${result.confidence} confidence` : "Preview only — not saved"}</div>
              </div>
            </div>
            {(result.competencies || []).length > 0 && <div className="space-y-3">{result.competencies.map((c, i) => <CompetencyBar key={i} {...c} />)}</div>}
            {result.rationale && <p className="text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{result.rationale}</p>}
          </div>

          {(result.question_breakdown || []).length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-6 space-y-3" data-testid="trial-breakdown">
              <div className="font-display font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Per-question results</div>
              {result.question_breakdown.map((b, i) => (
                <div key={i} className="rounded-lg border border-white/8 bg-white/[0.02] p-3 text-sm space-y-1">
                  <div className="flex items-start gap-2">
                    {b.format === "multiple_choice"
                      ? (b.is_correct ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : <X className="h-4 w-4 text-rose-400 mt-0.5 shrink-0" />)
                      : <Circle className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                    <span className="font-medium">{i + 1}. {b.prompt}</span>
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground space-y-0.5">
                    <div>Answer: {b.answer || "—"}</div>
                    {b.format === "multiple_choice"
                      ? (!b.is_correct && b.correct_answer && <div className="text-emerald-300">Correct: {b.correct_answer}</div>)
                      : (b.score != null && <div>Score: {b.score}/100{b.note ? ` · ${b.note}` : ""}</div>)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Proctoring integrity — flags captured during the run */}
          <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="trial-proctor-summary">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-display font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> Proctoring integrity</div>
              {result.integrity_score != null && (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${result.integrity_score >= 75 ? "bg-emerald-500/15 text-emerald-300" : result.integrity_score >= 40 ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`} data-testid="trial-integrity-badge">
                  {result.integrity_label} · {result.integrity_score}/100
                </span>
              )}
            </div>
            {(result.proctor_flags || []).length > 0 ? (
              <div className="space-y-2" data-testid="trial-proctor-flags">
                {result.proctor_flags.map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
                    <span className="text-sm flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" /> {f.detail}</span>
                    <span className="text-xs font-mono text-muted-foreground shrink-0">×{f.count}</span>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1"><Sparkles className="h-3 w-3 text-primary inline mr-1" />Advisory only — flags inform a human reviewer and never auto-reject a candidate.</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 className="h-4 w-4" /> No proctoring flags captured during this run.</div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => nav(`/recruiter/trial-report/${trialId}${backTo ? `?return=${encodeURIComponent(backTo)}` : ""}`)} data-testid="trial-report">View quality report</Button>
            <Button variant="outline" onClick={() => { submitRef.current = false; setResult(null); setReview(false); setIdx(0); }} data-testid="trial-retry">Run again</Button>
            <Button onClick={exit} data-testid="trial-back">{backTo ? "Back to conversation" : "Back to builder"}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Safe Exam Browser tests: launch the app, then verify its settings before anything else.
  if (sebMode && !inSebApp && !result) return <SebLaunchGate candId={candId} onExit={exit} />;
  if (sebMode && !sebOk && !result) return <SebVerifyGate candId={candId} onVerified={() => setSebOk(true)} />;
  // Lockdown tests need desktop Chrome/Edge, then start in full screen (user gesture).
  if (browserBlocked && !result) return <BrowserBlockGate mobile={support.mobile} onExit={exit} />;
  if (lockGates && !lockStarted && !result) return <FullscreenGate onEnter={() => document.documentElement.requestFullscreen?.().then(() => { setIsFs(true); setLockStarted(true); }).catch(() => toast.error("Full screen was blocked. Allow it in your browser and try again."))} onExit={exit} />;

  // Identity verification gate (per-assessment policy) — real candidate runs only.
  if (!trial && policy.identity_check && !idDone && !result) {
    return <IdentityGate candId={candId} jobTitle={job?.title} onDone={() => setIdDone(true)} onExit={exit} />;
  }

  // Screen-share gate (per-assessment policy) — needs a user gesture to start screen capture.
  if (proctored && needsScreen && !screenSkip && !result) {
    return <ScreenShareGate jobTitle={job?.title} status={screenStatus} onShare={startScreen} onSkip={() => setScreenSkipped(true)} onExit={exit} />;
  }

  return (
    <div className="dark fixed inset-0 z-[60] bg-[#070809] flex flex-col text-foreground" data-testid="exam-room" data-lockdown={lockdown ? "on" : "off"} data-seb={sebMode ? "on" : "off"}>
      {paused && <PausedOverlay />}
      {fsLost && !paused && <FullscreenGate returning onEnter={enterFs} />}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "48px 48px" }} aria-hidden />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between gap-3 px-4 md:px-6 h-14 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><FlaskConical className="h-4 w-4 text-primary" /></div>
          <div className="min-w-0">
            <div className="text-sm font-display font-semibold truncate">{job?.title || "Assessment"}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              {trial ? <span className="text-amber-400 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Trial run · proctored · {test?.source}</span> : proctored ? <span className="text-emerald-400 flex items-center gap-1"><motion.span animate={{ opacity: [1, 0.35, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="h-1.5 w-1.5 rounded-full bg-emerald-400" data-testid="proctor-active-dot" /> Proctoring active{sebMode ? " · Safe Exam Browser" : lockdown ? " · lockdown" : ""}</span> : "Assessment room"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className={`inline-flex items-center gap-1.5 text-sm font-mono ${remaining != null ? (remaining <= 20 ? "text-rose-400" : remaining <= 60 ? "text-amber-400" : "text-primary") : "text-muted-foreground"}`} data-testid="exam-timer"><Clock className="h-3.5 w-3.5" /> {paused ? "Paused" : remaining != null ? `${fmt(remaining)} left` : fmt(elapsed)}</span>
          <span className="hidden sm:inline text-xs font-mono text-muted-foreground" data-testid="exam-progress">{answered}/{questions.length} answered</span>
          <Button variant="ghost" size="sm" onClick={exit} data-testid="exam-exit" className="gap-1.5 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /> Exit</Button>
        </div>
      </header>

      {/* progress bar */}
      <div className="relative z-10 h-1 bg-white/5"><div className="h-full bg-primary transition-all" style={{ width: `${(answered / total) * 100}%` }} /></div>

      <div className="relative z-10 flex-1 overflow-hidden flex flex-col lg:flex-row w-full max-w-[1600px] mx-auto">
        {/* Proctor rail */}
        {proctored && (
          <aside className="lg:w-80 shrink-0 p-4 border-b lg:border-b-0 lg:border-r border-white/10 overflow-y-auto max-h-[38dvh] lg:max-h-none">
            <ProctorLive {...proctor} />
            <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-muted-foreground leading-relaxed">
              <Sparkles className="h-3.5 w-3.5 text-primary inline mr-1" /> Signals are advisory only. A human reviews every outcome — nothing here auto-rejects you.
            </div>
          </aside>
        )}

        {/* Question area */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-6 flex">
          <div className="max-w-3xl mx-auto w-full my-auto space-y-5">
            {!trial && proctored && <RuleWarning state={pstate} message={pstate?.last ? CALM[pstate.last.type] : null} />}
            {warned && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2.5" data-testid="proctor-warning">
                <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-100/90 leading-relaxed">We've noticed {events.length} monitoring signal{events.length === 1 ? "" : "s"} this session (e.g. leaving the tab or losing focus). These are <strong>advisory only</strong> and reviewed by a human — please stay on this tab and keep your workspace clear.</p>
              </div>
            )}
            {!review ? (
              <AnimatePresence mode="wait" initial={false}>
              <motion.div key={idx} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.18 }} className="space-y-5" data-testid="exam-question">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono uppercase tracking-widest text-primary">Question {idx + 1} of {questions.length}</span>
                  {q?.section && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-secondary text-muted-foreground">{q.section}</span>}
                  {q?.difficulty && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFF[q.difficulty] || ""}`}>{q.difficulty}</span>}
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-lg leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid="exam-question-prompt">{q?.prompt}</p>
                  {q?.assesses && <p className="text-xs text-primary mt-2">Assesses: {q.assesses}</p>}
                </div>
                {q?.format === "multiple_choice" && (q?.options || []).length > 0 ? (
                  <div className="space-y-2.5" data-testid="exam-mcq">
                    {q.options.map((opt, oi) => {
                      const selected = answers[q.key] === opt;
                      return (
                        <button key={oi} type="button" onClick={() => setAns(q.key, opt)} data-testid={`exam-option-${oi}`}
                          className={`w-full text-left rounded-xl border p-4 flex items-start gap-3 transition-colors ${selected ? "border-primary bg-primary/10" : "border-white/10 bg-white/[0.03] hover:border-primary/40"}`}>
                          <span className={`mt-0.5 h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-primary bg-primary" : "border-white/30"}`}>{selected && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}</span>
                          <span className="text-base leading-relaxed">{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Textarea value={answers[q?.key] || ""} onChange={(e) => setAns(q.key, e.target.value)} rows={9}
                    placeholder="Take your time — your progress autosaves and you can safely resume if disconnected."
                    data-testid="exam-answer" className="bg-black/30 border-white/10 text-base" />
                )}
                <div className="sticky bottom-0 z-10 -mx-2 px-2 py-3 flex items-center justify-between gap-3 bg-[#070809]/90 backdrop-blur" data-testid="exam-nav">
                  <Button variant="outline" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="exam-prev" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Previous</Button>
                  {idx < questions.length - 1 ? (
                    <Button onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))} data-testid="exam-next" className="gap-1.5">Next <ArrowRight className="h-4 w-4" /></Button>
                  ) : (
                    <Button onClick={() => setReview(true)} data-testid="exam-review" className="gap-1.5"><ListChecks className="h-4 w-4" /> Review &amp; submit</Button>
                  )}
                </div>
              </motion.div>
              </AnimatePresence>
            ) : (
              <div className="space-y-4" data-testid="exam-review-panel">
                <h2 className="text-xl font-display font-semibold">Review your answers</h2>
                <p className="text-sm text-muted-foreground">{answered} of {questions.length} answered. Tap any question to edit.</p>
                <div className="space-y-2">
                  {questions.map((x, i) => {
                    const done = (answers[x.key] || "").trim();
                    return (
                      <button key={x.key} onClick={() => { setReview(false); setIdx(i); }} data-testid={`exam-review-item-${i}`}
                        className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] p-3.5 hover:border-primary/40 transition-colors flex items-start gap-3">
                        {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-sm font-medium line-clamp-1">{i + 1}. {x.prompt}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{done ? answers[x.key] : "Not answered yet"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="sticky bottom-0 z-10 -mx-2 px-2 py-3 flex items-center justify-between gap-3 bg-[#070809]/90 backdrop-blur" data-testid="exam-submit-bar">
                  <Button variant="outline" onClick={() => setReview(false)} data-testid="exam-back-to-questions"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
                  <Button onClick={submit} disabled={busy || !allAnswered} data-testid="exam-submit" className="gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {trial ? "Submit trial" : "Submit assessment"}
                  </Button>
                </div>
                {!allAnswered && <p className="text-xs text-amber-400 text-right">Answer every question to submit.</p>}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}


function IdentityGate({ candId, jobTitle, onDone, onExit }) {
  const [idImg, setIdImg] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(null);
  const [rec, setRec] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const r = new FileReader();
        r.onload = () => setVoice(r.result);
        r.readAsDataURL(blob);
      };
      recRef.current = mr;
      mr.start();
      setRec(true);
      setTimeout(() => { if (mr.state === "recording") mr.stop(); setRec(false); }, 6000);
    } catch { toast.error("Microphone unavailable — you can skip the voice sample"); }
  };
  const stopRec = () => { if (recRef.current?.state === "recording") recRef.current.stop(); setRec(false); };

  const readFile = (file, setter) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!idImg || !selfie) { toast.error("Add both your photo ID and a selfie"); return; }
    setBusy(true);
    try {
      await api.post(`/candidates/${candId}/identity`, { id_image_base64: idImg, selfie_base64: selfie, voice_enroll_base64: voice });
      toast.success("Identity submitted — a reviewer will confirm it. You can start now.");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit — please retry"); }
    finally { setBusy(false); }
  };

  const Drop = ({ img, onFile, label, testid }) => (
    <label className="flex-1 cursor-pointer" data-testid={testid}>
      <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-white/15 bg-white/[0.03] flex flex-col items-center justify-center gap-2 overflow-hidden hover:border-primary/50 transition-colors">
        {img ? <img src={img} alt={label} className="h-full w-full object-cover" /> : (
          <><Upload className="h-6 w-6 text-muted-foreground" /><span className="text-xs text-muted-foreground">{label}</span></>
        )}
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
    </label>
  );

  return (
    <div className="dark fixed inset-0 z-[60] bg-[#070809] overflow-y-auto text-foreground" data-testid="identity-gate">
      <div className="max-w-xl mx-auto px-4 py-10 space-y-6">
        <div className="text-center space-y-2">
          <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center mx-auto"><ScanFace className="h-6 w-6 text-primary" /></div>
          <h1 className="text-2xl font-display font-bold">Verify your identity</h1>
          <p className="text-sm text-muted-foreground">This assessment{jobTitle ? ` for ${jobTitle}` : ""} asks you to confirm who you are. Upload a photo ID and a selfie — a human reviewer checks them. If you can't, you can exit and ask for a manual-review alternative.</p>
        </div>
        <div className="flex gap-4">
          <Drop img={idImg} onFile={(f) => readFile(f, setIdImg)} label="Photo ID" testid="identity-id-drop" />
          <Drop img={selfie} onFile={(f) => readFile(f, setSelfie)} label="Selfie" testid="identity-selfie-drop" />
        </div>
        <p className="text-[11px] text-muted-foreground text-center">Your photos are stored securely for review only and deleted per your org's retention policy. No facial-recognition decision is automated.</p>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2" data-testid="voice-enroll">
          <div className="text-sm font-medium flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> Voice sample <span className="text-xs text-muted-foreground font-normal">(optional)</span></div>
          <p className="text-xs text-muted-foreground">Read aloud: <span className="text-foreground">"I confirm I am taking this assessment myself."</span> It helps flag if a clearly different voice is heard later. Advisory only — never used as proof of identity.</p>
          <div className="flex items-center gap-2">
            {!voice ? (
              <Button size="sm" variant="outline" onClick={rec ? stopRec : startRec} data-testid="voice-record-btn" className="gap-1.5">{rec ? <><Square className="h-3.5 w-3.5" /> Stop</> : <><Mic className="h-3.5 w-3.5" /> Record (up to 6s)</>}</Button>
            ) : (
              <><audio controls src={voice} className="h-8" data-testid="voice-playback" /><Button size="sm" variant="ghost" onClick={() => setVoice(null)} data-testid="voice-clear">Redo</Button></>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={onExit} data-testid="identity-exit">Exit &amp; request manual review</Button>
          <Button onClick={submit} disabled={busy} data-testid="identity-submit" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit &amp; start</Button>
        </div>
      </div>
    </div>
  );
}

function ScreenShareGate({ jobTitle, status, onShare, onSkip, onExit }) {
  const [busy, setBusy] = useState(false);
  const share = async () => { setBusy(true); try { await onShare(); } finally { setBusy(false); } };
  return (
    <div className="dark fixed inset-0 z-[60] bg-[#070809] overflow-y-auto text-foreground" data-testid="screen-gate">
      <div className="max-w-xl mx-auto px-4 py-10 space-y-6">
        <div className="text-center space-y-2">
          <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center mx-auto"><MonitorPlay className="h-6 w-6 text-primary" /></div>
          <h1 className="text-2xl font-display font-bold">Share your screen to begin</h1>
          <p className="text-sm text-muted-foreground">This assessment{jobTitle ? ` for ${jobTitle}` : ""} records your screen for integrity review. When prompted, choose to share your <strong>entire screen</strong>. The recording is advisory and reviewed by a human — nothing here auto-rejects you.</p>
        </div>
        {status === "error" && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200 text-center" data-testid="screen-error">
            Screen sharing wasn't granted or was stopped. You can retry, or continue — your choice is logged for the reviewer.
          </div>
        )}
        <p className="text-[11px] text-muted-foreground text-center">Your recording is stored securely for review only and deleted per your org's retention policy.</p>
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={onSkip} data-testid="screen-skip">Continue without sharing</Button>
          <Button onClick={share} disabled={busy} data-testid="screen-share" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MonitorPlay className="h-4 w-4" />} Share screen &amp; begin</Button>
        </div>
        <button onClick={onExit} className="text-xs text-muted-foreground hover:text-foreground mx-auto block" data-testid="screen-exit">Exit assessment</button>
      </div>
    </div>
  );
}
